import {
  buildingDashboardApi,
  DashboardBuildingCPX,
  DashboardBuildingEnvironmental,
  DashboardBuildingEnvironmentalIN,
  DashboardBuildingMass,
  DashboardBuildingMaterialValue,
  DashboardBuildingOperationalEnergy,
  DashboardBuildingGeneralEmissions,
  getLocalAuthHeader,
  DashboardBuildingOperationalEmissions,
} from "api-client";
import { makeAutoObservable, runInAction } from "mobx";
import { buildingStore } from "./BuildingStore";
import { SelectOption } from "components/DropdownSelect";
import { b6ImporterStore } from "store/B6ImporterStore";
import { GWP_M2_AREA_UNIT, GWP_M2_UNIT, GWP_UNIT, KG_M2_UNIT } from "utils";

export const categorySystemOptions: SelectOption[] = [
  {
    id: "material_name",
    name: "Material",
    name_de: "Materialien",
    value: "material_name",
  },
  {
    id: "ifc_entity",
    name: "IFC Entity",
    name_de: "IFC Entitäten",
    value: "ifc_entity",
  },
  {
    id: "din_category_name",
    name: "DIN 276",
    name_de: "DIN 276",
    value: "din_category_name",
  },
];

class BuildingDashboardStore {
  cpxInfo: DashboardBuildingCPX | null = null;
  operationalEnergyInfo: DashboardBuildingOperationalEnergy | null = null;
  operationalEmissionsInfo: DashboardBuildingOperationalEmissions | null = null;
  hasB6Report: boolean = false;
  // -- Emissions panel
  emissionsInfo: DashboardBuildingGeneralEmissions | null = null;
  get emissionsAxisRange(): number[] {
    const findMin = (list: number[]) => {
      return list.reduce((prev, current) => Math.min(prev, current), 0);
    };
    const findMax = (list: number[]) => {
      return list.reduce((prev, current) => Math.max(prev, current), 0);
    };
    let minGwp = 0,
      maxGwp = 0,
      minPenrt = 0,
      maxPenrt = 0;

    if (this.emissionsInfo?.gwp_traces) {
      const points = this.emissionsInfo.gwp_traces
        .map((e) => e.y)
        .flat() as number[];
      minGwp = findMin(points);
      maxGwp = findMax(points);
    }

    if (this.emissionsInfo?.operational_energy_traces) {
      const points = this.emissionsInfo.operational_energy_traces
        .map((e) => e.y)
        .flat() as number[];
      minPenrt = findMin(points);
      maxPenrt = findMax(points);
    }

    const range = [Math.min(minGwp, minPenrt), Math.max(maxGwp, maxPenrt)];
    return range;
  }

  // -- Environmental panel
  environmentalInfo: DashboardBuildingEnvironmental | null = null;
  environmentalCategorySystemSelected: string = "material_name";
  environmentalUnitSelected: string = "kg CO2e/m2*a";
  environmentalUnitOptions: SelectOption[] = [
    {
      id: "kg CO2e/m2*a",
      name: GWP_M2_AREA_UNIT,
      name_de: GWP_M2_AREA_UNIT,
      value: "kg CO2e/m2*a",
    },
    {
      id: "kg CO2e/m2",
      name: GWP_M2_UNIT,
      name_de: GWP_M2_UNIT,
      value: "kg CO2e/m2",
    },
    { id: "kg CO2e", name: GWP_UNIT, name_de: GWP_UNIT, value: "kg CO2e" },
  ];

  // -- Mass panel
  massInfo: DashboardBuildingMass | null = null;
  massCategorySystemSelected: string = "material_name";
  massUnitSelected: string = "t";
  massUnitOptions: SelectOption[] = [
    { id: "kg", name: "kg", name_de: "kg", value: "kg" },
    { id: "kg/m2", name: KG_M2_UNIT, name_de: KG_M2_UNIT, value: "kg/m2" },
    { id: "t", name: "t", name_de: "t", value: "t" },
  ];

  // -- Material value panel
  materialValueInfo: DashboardBuildingMaterialValue | null = null;
  materialValueCategorySelected: string = "material_name";

  async checkB6HasReport() {
    let hasReport: Boolean | void = false;
    hasReport = await b6ImporterStore.hasReportData();
    if (typeof hasReport === "boolean") this.hasB6Report = hasReport;
    if (this.hasB6Report) {
      await this.fetchGeneralEmissions(this.currentBuildingID);
      await this.fetchOperationalEmissions(this.currentBuildingID);
    }
  }

  get materialValuePercentage(): number | null {
    if (!this.materialValueInfo?.object_count_with_match) {
      return null;
    }
    const numerator =
      this.materialValueInfo.object_count_with_match_and_material_price ?? 0;
    const denominator = this.materialValueInfo.object_count_with_match;

    const result = (numerator / denominator) * 100;
    return result;
  }

  get massWithValuePercentage(): number | null {
    if (!this.materialValueInfo?.total_mass) {
      return null;
    }
    const numerator =
      this.materialValueInfo.total_mass_with_material_price ?? 0;
    const denominator = this.materialValueInfo.total_mass;

    const result = (numerator / denominator) * 100;
    return result;
  }

  get currentBuildingID(): string | undefined {
    return buildingStore.currentBuilding?.id;
  }

  get environmentalDashboardConfig(): DashboardBuildingEnvironmentalIN {
    return {
      unit: this.environmentalUnitSelected,
      category_system: this.environmentalCategorySystemSelected,
    };
  }

  setEnvironmentalUnitSelected(newValue: string) {
    runInAction(() => {
      this.environmentalUnitSelected = newValue;
      this.fetchEnvironmental(this.currentBuildingID);
    });
  }

  setEnvironmentalCategorySystemSelected(newValue: string) {
    runInAction(() => {
      this.environmentalCategorySystemSelected = newValue;
      this.fetchEnvironmental(this.currentBuildingID);
    });
  }

  setMassUnitSelected(newValue: string) {
    runInAction(() => {
      this.massUnitSelected = newValue;
      this.fetchMass(this.currentBuildingID);
    });
  }

  setMassCategorySystemSelected(newValue: string) {
    runInAction(() => {
      this.massCategorySystemSelected = newValue;
      this.fetchMass(this.currentBuildingID);
    });
  }

  setMaterialValueCategorySelected(newValue: string) {
    runInAction(() => {
      this.materialValueCategorySelected = newValue;
      this.fetchMaterialValue(this.currentBuildingID);
    });
  }

  async fetchMaterialValue(
    building_id: string | undefined
  ): Promise<DashboardBuildingMaterialValue | void> {
    if (!building_id || !this.materialValueCategorySelected) return;
    const authHeader = getLocalAuthHeader();
    return await buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingMaterialValue(
        building_id,
        { category_system: this.materialValueCategorySelected },
        authHeader
      )
      .then((response) => response.data)
      .then((value) =>
        runInAction(() => {
          this.materialValueInfo = value;
        })
      )
      .catch((err) => console.error(err, "error.fetchMaterialValue"));
  }

  async fetchCpx(
    building_id: string | undefined
  ): Promise<DashboardBuildingCPX | void> {
    if (!building_id) return;
    const authHeader = getLocalAuthHeader();
    return buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingCpx(building_id, authHeader)
      .then((response) => response.data)
      .then((value) =>
        runInAction(() => {
          this.cpxInfo = value;
        })
      )
      .catch((err) => console.error(err, "error.fetchCpx"));
  }

  async fetchEnvironmental(
    building_id: string | undefined
  ): Promise<DashboardBuildingEnvironmental | void> {
    if (!building_id) return;
    const authHeader = getLocalAuthHeader();
    return buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingEnvironmental(
        building_id,
        {
          unit: this.environmentalUnitSelected,
          category_system: this.environmentalCategorySystemSelected,
        },
        authHeader
      )
      .then((response) => response.data)
      .then((value) =>
        runInAction(() => {
          this.environmentalInfo = value;
        })
      )
      .catch((err) => console.error(err, "error.fetchEnvironmental"));
  }

  async fetchMass(
    building_id: string | undefined
  ): Promise<DashboardBuildingMass | void> {
    if (!building_id) return;
    const authHeader = getLocalAuthHeader();
    return buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingMass(
        building_id,
        {
          category_system: this.massCategorySystemSelected,
          unit: this.massUnitSelected,
        },
        authHeader
      )
      .then((response) => response.data)
      .then((value) =>
        runInAction(() => {
          this.massInfo = value;
        })
      )
      .catch((err) => console.error(err, "error.fetchEnvironmental"));
  }

  async fetchOperationalEnergy(
    building_id: string | undefined
  ): Promise<DashboardBuildingOperationalEnergy | void> {
    if (!building_id) return;
    const authHeader = getLocalAuthHeader();
    return buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingOperationalEnergy(
        building_id,
        authHeader
      )
      .then((response) => response.data)
      .then((value) =>
        runInAction(() => {
          this.operationalEnergyInfo = value;
        })
      )
      .catch((err) => console.error(err, "error.fetchOperationalEnergy"));
  }

  async fetchOperationalEmissions(
    building_id: string | undefined
  ): Promise<DashboardBuildingOperationalEmissions | void> {
    if (!building_id) return;
    const authHeader = getLocalAuthHeader();
    return buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingOperationalEmissions(
        building_id,
        authHeader
      )
      .then((response) => response.data)
      .then((value) =>
        runInAction(() => {
          this.operationalEmissionsInfo = value;
        })
      )
      .catch((err) => console.error(err, "error.BuildingOperationalEmissions"));
  }

  async fetchGeneralEmissions(
    building_id: string | undefined
  ): Promise<DashboardBuildingGeneralEmissions | void> {
    if (!building_id) return;
    const authHeader = getLocalAuthHeader();
    return buildingDashboardApi
      .reportApiV1RoutersDashboardBuildingGeneralEmissions(
        building_id,
        authHeader
      )
      .then((response) => response.data)
      .then((value) =>
        runInAction(() => {
          this.emissionsInfo = value;
        })
      )
      .catch((err) => console.error(err, "error.fetchGeneralEmissions"));
  }

  async initialFetch() {
    const buildingId = this.currentBuildingID;
    await Promise.all([
      this.fetchMaterialValue(buildingId),
      this.fetchCpx(buildingId),
      this.fetchEnvironmental(buildingId),
      this.fetchGeneralEmissions(buildingId),
      this.fetchMass(buildingId),
      this.fetchOperationalEnergy(buildingId),
      this.fetchOperationalEmissions(buildingId),
    ]);
  }

  static instance: BuildingDashboardStore;

  constructor() {
    makeAutoObservable(this);
  }

  static getInstance(): BuildingDashboardStore {
    if (!BuildingDashboardStore.instance) {
      BuildingDashboardStore.instance = new BuildingDashboardStore();
    }
    return BuildingDashboardStore.instance;
  }
}

export const buildingDashboardStore = BuildingDashboardStore.getInstance();
